/* The contract manager setting a new password for one of their pharmacies.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHAT THIS PAGE IS FOR, IN ONE SENTENCE.
 *
 * A pharmacist rings their own contract manager instead of ringing Izy, and
 * the manager fixes it in about fifteen seconds.
 *
 * It exists because the forced password change came out (drizzle/0050). The
 * password Izy generates for a portal account now works until somebody
 * chooses to change it, which means the recovery path stopped being a nice
 * idea and became the whole control. If this page does not work, the only
 * remedy for a forgotten portal password is a telephone call to us.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE PASSWORD IS SHOWN, ON PURPOSE, AND ONLY HERE.
 *
 * Whoever resets it has to read it out or type it into a message, so hiding
 * it behind dots would mean they could not do the one thing the page is for.
 * It is never stored, never audited and never sent back by the server after
 * this moment: close the page and it is gone.
 *
 * Generated by default rather than typed, because a manager inventing eight
 * passwords in a row invents eight variations of the pharmacy name. They can
 * still type their own -- some places have a convention, and fighting it
 * produces a password on a sticky note.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE LIST IS THE SERVER'S ANSWER, NOT A GUESS.
 *
 * Every name on it came from GET /portals, which applies all five of the
 * tests in modules/uh/portal-reset.ts. The page never builds a username, so
 * there is no way for it to offer something the server would refuse, and no
 * second opinion about who counts as one of your own counters.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { SecretInput } from '../../app/SecretInput';
import { Pager, usePaged } from '../../app/Pager';

interface Portal {
    username: string;
    name: string;
    status: 'active' | 'disabled';
    pharmacies: string[];
}

/* Read off a screen once and typed into a message. No lookalike characters:
   somebody squinting at l against 1 is the support call this page exists to
   prevent. Mirrors the alphabet in scripts/create-pharmacy-portals.mjs. */
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

function generated(): string {
    const bytes = new Uint8Array(14);
    crypto.getRandomValues(bytes);
    let out = '';
    for (const b of bytes) out += ALPHABET[b % ALPHABET.length];
    /* Shaped to clear the server's eight-character floor with room to spare,
       and punctuated so it reads as three chunks down a telephone. */
    return `Portal-${out.slice(0, 10)}-${out.slice(10, 14)}!`;
}

export function PortalLogins() {
    const { code = 'uh' } = useParams();
    const base = `/api/projects/${code}/uh/portals`;

    const [portals, setPortals] = useState<Portal[] | null>(null);
    /* Eight counters today, so the control stays hidden. It is here for
       the ninth: a contract that adds a site should not be the thing
       that discovers this table was never paged. */
    const paged = usePaged(portals ?? []);
    const [error, setError] = useState<string | null>(null);

    /* Which row is open, and what is in its box. One at a time: a page with
       eight password fields filled in is a page somebody screenshots. */
    const [open, setOpen] = useState<string | null>(null);
    const [draft, setDraft] = useState('');
    const [busy, setBusy] = useState(false);

    /* The result of the last reset, kept on the page so the manager can read
       it out after the form has closed. Never re-fetched: the server does not
       keep it either. */
    const [done, setDone] = useState<{ username: string; name: string; password: string; revoked: number } | null>(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            setPortals(await api<Portal[]>(base));
        } catch (err) {
            /* The honest version of a 403 here. Somebody who reached this
                page without the capability is not doing anything wrong --
                they followed a link or a bookmark -- and "not authorised"
                tells them nothing they can act on. */
            setError(err instanceof ApiError && err.code === 'portal.notAManager'
                ? 'Your login cannot change other pharmacies’ passwords. Only the contract manager’s can.'
                : err instanceof ApiError ? err.message : 'Could not load your pharmacies.');
            setPortals([]);
        }
    }, [base]);
    useEffect(() => { void load(); }, [load]);

    const begin = (p: Portal) => {
        setDone(null);
        setError(null);
        setOpen(p.username);
        setDraft(generated());
    };

    const cancel = () => {
        setOpen(null);
        setDraft('');
    };

    const submit = async (p: Portal) => {
        setBusy(true);
        setError(null);
        try {
            const res = await api<{ revokedSessions: number }>(`${base}/${encodeURIComponent(p.username)}/password`, {
                method: 'POST', json: { password: draft },
            });
            setDone({ username: p.username, name: p.name, password: draft, revoked: res.revokedSessions });
            setOpen(null);
            setDraft('');
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Could not change that password.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <h1>Pharmacy logins</h1>
            <p className="izy-sub">
                Set a new password for any of your pharmacies. Use this when somebody has forgotten theirs,
                when a member of staff has left, or when a password has been sent to the wrong person.
            </p>

            {error !== null && <div className="izy-alert error" role="alert">{error}</div>}

            {/* The whole point of the page, so it sits above the list rather
                than below it: after a reset the manager needs to read this
                out, and a confirmation they have to scroll back up to find is
                a confirmation they will lose. */}
            {done !== null && (
                <div className="izy-card">
                    <h2>New password for {done.name}</h2>
                    <p>
                        Send this to the pharmacy, then delete the message. It is not stored anywhere and
                        we cannot show it to you again: if it is lost, set another one.
                    </p>
                    <table className="izy-table">
                        <tbody>
                            <tr>
                                <th scope="row">Username</th>
                                <td><code>{done.username}</code></td>
                            </tr>
                            <tr>
                                <th scope="row">Password</th>
                                <td><code>{done.password}</code></td>
                            </tr>
                        </tbody>
                    </table>
                    <p className="izy-muted">
                        {done.revoked === 0
                            ? 'Nobody was signed in as that pharmacy, so nobody was interrupted.'
                            : `${done.revoked} ${done.revoked === 1 ? 'person was' : 'people were'} signed out of that pharmacy and will need the new password.`}
                    </p>
                </div>
            )}

            <div className="izy-card">
                <h2>Your pharmacies</h2>
                {portals === null ? <div className="izy-muted">Loading...</div>
                    : portals.length === 0 ? (
                        <p className="izy-muted">
                            No pharmacy logins are listed against your account. If that looks wrong, tell us
                            and we will check what your login covers.
                        </p>
                    ) : (
                        <>
                        <table className="izy-table">
                            <thead>
                                <tr>
                                    <th scope="col">Pharmacy</th>
                                    <th scope="col">Username</th>
                                    <th scope="col">New password</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.rows.map((p) => (
                                    <tr key={p.username}>
                                        <td>
                                            {p.pharmacies.join(', ') || p.name}
                                            {p.status === 'disabled' && (
                                                <> <span className="izy-pill off">Disabled</span></>
                                            )}
                                        </td>
                                        <td><code>{p.username}</code></td>
                                        <td>
                                            {open === p.username ? (
                                                <form
                                                    className="izy-row"
                                                    onSubmit={(e) => { e.preventDefault(); void submit(p); }}
                                                >
                                                    {/* Revealable like every other password box in the
                                                        system, and for a sharper reason here: the person
                                                        typing it has to be able to read it back. */}
                                                    <SecretInput
                                                        label="New password"
                                                        autoComplete="off"
                                                        value={draft}
                                                        onChange={(e) => setDraft(e.target.value)}
                                                        minLength={8}
                                                        required
                                                    />
                                                    <button className="izy-btn" type="submit" disabled={busy || draft.length < 8}>
                                                        {busy ? 'Setting...' : 'Set it'}
                                                    </button>
                                                    <button className="izy-btn secondary" type="button" onClick={cancel} disabled={busy}>
                                                        Cancel
                                                    </button>
                                                </form>
                                            ) : (
                                                <button className="izy-btn secondary" type="button" onClick={() => begin(p)}>
                                                    Set a new password
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <Pager of={paged} noun="pharmacies" />
                        </>
                    )}
            </div>

            <p className="izy-muted">
                Changing a password signs that pharmacy out everywhere immediately, which is usually the
                reason for doing it. Every change is recorded against your name and the pharmacy, and the
                password itself is never recorded. Your own password is not changed from here: use
                Your account.
            </p>
        </>
    );
}
